import { DataReportUsecase } from "./data_report"

// 浏览器安全模型禁止 JS 读取 SSID、信号强度、频段、是否以太网等真实信息，
// navigator.connection.type 在 Chromium 上恒为 undefined（仅暴露 effectiveType
// 这种蜂窝等级指标）。因此 web 端 network_change 只能上报 network_type，
// 且 Wi-Fi 是基于"浏览器客户端主要在 Wi-Fi"的推断（无法可靠区分以太网）。
// 对齐 android 端字段：wifi_ssid / wifi_signal_level / wifi_freq_band 在 web
// 端无法获取，故不上报这些字段。
type NetworkType = "None" | "Wi-Fi" | "Ethernet"

interface BrowserNetworkInformation extends EventTarget {
  type?: string
}

interface NetworkStatus {
  networkType: NetworkType
}

const POLL_INTERVAL_MS = 5_000
let timer: number | null = null
let lastStatusKey: string | null = null
let reporting = false

function getConnection(): BrowserNetworkInformation | undefined {
  return (navigator as Navigator & { connection?: BrowserNetworkInformation })
    .connection
}

function getStatus(): NetworkStatus {
  if (!navigator.onLine) return { networkType: "None" }

  switch (getConnection()?.type?.toLowerCase()) {
    case "ethernet":
      return { networkType: "Ethernet" }
    case "wifi":
      return { networkType: "Wi-Fi" }
    default:
      // Chromium 常不暴露 NetworkInformation.type（恒 undefined）。浏览器客户端
      // 绝大多数在 Wi-Fi 上，此处以 Wi-Fi 作为默认推断；若浏览器未来暴露
      // ethernet，上面分支会优先命中。这是平台限制下的尽力而为，非真实判定。
      return { networkType: "Wi-Fi" }
  }
}

async function reportIfChanged(): Promise<void> {
  if (reporting) return
  reporting = true
  try {
    const status = getStatus()
    const key = JSON.stringify(status)
    if (key === lastStatusKey) return
    lastStatusKey = key

    const data: Record<string, string | number> = {
      network_type: status.networkType,
    }
    // wifi_ssid / wifi_signal_level / wifi_freq_band 因浏览器安全限制无法获取，
    // 不在此上报（对齐 android 字段定义，但值缺省）。
    await DataReportUsecase.reportLog("network_change", data)
  } finally {
    reporting = false
  }
}

function onNetworkChange(): void {
  void reportIfChanged()
}

export const NetworkStatusUsecase = {
  start(): void {
    if (timer !== null) return
    window.addEventListener("online", onNetworkChange)
    window.addEventListener("offline", onNetworkChange)
    getConnection()?.addEventListener("change", onNetworkChange)
    void reportIfChanged()
    timer = window.setInterval(() => {
      reportIfChanged().catch(console.error)
    }, POLL_INTERVAL_MS)
  },
  stop(): void {
    if (timer === null) return
    window.removeEventListener("online", onNetworkChange)
    window.removeEventListener("offline", onNetworkChange)
    getConnection()?.removeEventListener("change", onNetworkChange)
    window.clearInterval(timer)
    timer = null
    lastStatusKey = null
  },
}
